import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layers, Package, ShoppingBag, Euro, TrendingUp, Clock, ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';

function StatCard({ icon: Icon, label, value, color, delay }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }} className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md transition-shadow">
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      <p className="text-2xl font-bold text-slate-900 mt-3">{value}</p>
      <p className="text-sm text-slate-500 mt-0.5">{label}</p>
    </motion.div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/admin/dashboard').then(r => setData(r.data)).catch(console.error);
  }, []);

  if (!data) return <div className="flex items-center justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-violet-600 border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-3xl font-bold tracking-tight text-slate-900">Tableau de bord</h1>
      <p className="text-slate-500 mt-1">Vue d'ensemble de votre activité</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-8">
        <StatCard icon={Layers} label="TCG enregistrés" value={data.total_tcgs} color="bg-violet-500" delay={0} />
        <StatCard icon={Package} label="Extensions ouvertes" value={data.open_extensions} color="bg-emerald-500" delay={0.05} />
        <StatCard icon={ShoppingBag} label="Précommandes" value={data.total_preorders} color="bg-sky-500" delay={0.1} />
        <StatCard icon={Euro} label="CA prévisionnel" value={`${data.total_revenue.toFixed(2)} \u20AC`} color="bg-amber-500" delay={0.15} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-10">
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-lg text-slate-900">Extensions actives</h2>
            <Link to="/admin/extensions" className="text-sm text-violet-600 hover:text-violet-700 font-medium flex items-center gap-1" data-testid="view-all-extensions">
              Tout voir <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {data.active_extensions.length === 0 ? (
            <p className="text-sm text-slate-400 py-8 text-center">Aucune extension active</p>
          ) : (
            <div className="space-y-3">
              {data.active_extensions.slice(0, 5).map(ext => (
                <Link key={ext.id} to={`/admin/extensions/${ext.id}`} className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 transition-colors group" data-testid={`dash-ext-${ext.id}`}>
                  <div>
                    <p className="font-medium text-sm text-slate-900 group-hover:text-violet-600 transition-colors">{ext.name}</p>
                    {ext.tcg_name && <p className="text-xs text-slate-500">{ext.tcg_name}</p>}
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="secondary" className="text-xs">{ext.preorder_count} cmd</Badge>
                    <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-violet-400 transition-colors" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <h2 className="font-bold text-lg text-slate-900 mb-4">Dernières précommandes</h2>
          {data.recent_preorders.length === 0 ? (
            <p className="text-sm text-slate-400 py-8 text-center">Aucune précommande</p>
          ) : (
            <div className="space-y-3">
              {data.recent_preorders.slice(0, 5).map(po => (
                <div key={po.id} className="flex items-center justify-between p-3 rounded-xl bg-slate-50" data-testid={`dash-po-${po.id}`}>
                  <div>
                    <p className="font-medium text-sm text-slate-900">{po.first_name} {po.last_name}</p>
                    <p className="text-xs text-slate-500">{po.extension_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-sm text-violet-600">{po.total.toFixed(2)} €</p>
                    <p className="text-xs text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(po.created_at).toLocaleDateString('fr-FR')}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
